import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { filterLanguagesByEventType } from '@application/helpers/language-filter-helper';
import { EventTypeEnum } from '@application/enums/event-type-enum';
import { Icon } from '@iconify/react';
import { Box, Card, CardContent, CardHeader, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { TableDocument } from 'iconsax-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EventPreview from './EventPreview';
import StartScreenContent from './StartScreenContent';
import useRightPanelStyles from './useRightPanelStyles';
import { useAppSelector } from '@application/store/useStore';
const RightPanel = ({ watch, backgroundImages = [], onBackgroundImagesChange, onPreviewLanguageChange, selectedLanguageId, entryMode, hasStepParam = false, eventId }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isMobile = useMediaQuery(theme.breakpoints.down(1280));
    const styles = useRightPanelStyles();
    const [open, setOpen] = useState(false);
    const showEventPreview = Boolean(hasStepParam);
    // Bind to event languages from Redux
    const eventLanguages = useAppSelector((state) => state.VDKEvent.VDKEvent?.eventLanguages) || [];
    const allLanguages = useAppSelector((state) => state.languages.languages) || [];
    const vdkEvent = useAppSelector((state) => state.VDKEvent.VDKEvent);
    const isFamilyEvent = vdkEvent?.type === EventTypeEnum.Family;
    // Filter languages based on event type
    const filteredEventLanguages = filterLanguagesByEventType(eventLanguages, isFamilyEvent);
    const filteredAllLanguages = filterLanguagesByEventType(allLanguages, isFamilyEvent);
    const availableLanguages = filteredEventLanguages && filteredEventLanguages.length > 0 ? filteredEventLanguages : filteredAllLanguages;
    // Determine single display language:
    // - If a languageId is provided (coming from route for preview), show that language
    // - Else show primary event language (or fallback to first available)
    const displayLanguage = useMemo(() => {
        if (selectedLanguageId) {
            return ((availableLanguages || []).find((l) => l.id === selectedLanguageId) ||
                availableLanguages[0]);
        }
        const primary = (eventLanguages || []).find((l) => l.isPrimary);
        return primary || availableLanguages[0];
    }, [selectedLanguageId, availableLanguages, eventLanguages]);
    const translationLocale = useMemo(() => {
        if (entryMode !== 'translationPreview' || !displayLanguage)
            return undefined;
        const raw = displayLanguage.locale || displayLanguage.code || '';
        return raw.split('-')[0].toLowerCase() || undefined;
    }, [entryMode, displayLanguage]);
    const handleClick = () => {
        if (isMobile) {
            setOpen((prev) => !prev);
            setTimeout(() => setOpen(false), 2000); // auto-close after 2s
        }
    };
    return (_jsxs(Card, { sx: { ...styles.card }, children: [_jsx(CardHeader, { avatar: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [_jsx(TableDocument, { variant: 'Outline', color: theme.palette.primary.darker, fontSize: 26 }), _jsx(Typography, { variant: 'h5', fontWeight: 500, color: theme.palette.text.primary, children: t('actions.preview') })] }), action: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, height: '100%' }, children: [_jsx(Tooltip, { title: t('tooltips.formStyleAndPreview'), placement: 'top', arrow: true, open: isMobile ? open : undefined, disableHoverListener: isMobile, disableFocusListener: isMobile, disableTouchListener: isMobile, children: _jsx("span", { style: { display: 'flex', alignItems: 'center' }, children: _jsx(Icon, { icon: 'hugeicons:information-circle', color: theme.palette.primary.darker, fontSize: 26, cursor: 'pointer', onClick: handleClick }) }) }), displayLanguage ? (_jsxs(Box, { sx: {
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                                padding: '4px 8px',
                                borderRadius: 1
                            }, children: [_jsx(Box, { component: 'img', src: displayLanguage?.flagUrl || '/assets/images/flags/us-flag.svg', alt: displayLanguage?.code, sx: {
                                        width: 20,
                                        height: 15,
                                        objectFit: 'cover',
                                        borderRadius: 0.5
                                    } }), _jsx(Typography, { variant: 'body2', color: theme.palette.text.primary, fontWeight: 500, sx: { textTransform: 'none' }, children: (displayLanguage?.code || 'en').toUpperCase() })] })) : null] }), sx: {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    padding: '0px 0px 16px 0px',
                    '& .MuiCardHeader-action': {
                        all: 'unset',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }
                }, disableTypography: true }), _jsx(CardContent, { sx: { ...styles.cardContentContainer }, children: showEventPreview ? (_jsx(EventPreview, { watch: watch, backgroundImages: backgroundImages, selectedLanguageId: selectedLanguageId })) : (_jsx(StartScreenContent, { watch: watch, backgroundImages: backgroundImages, onBackgroundImagesChange: onBackgroundImagesChange, entryMode: entryMode, translationLocale: translationLocale })) })] }));
};
export default RightPanel;
